import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import TopNav from '../../components/layout/TopNav';
import api from '../../services/api';
import PartnerIntegrationsPanel from './PartnerIntegrationsPanel';
import ScoringProfilesPanel from './ScoringProfilesPanel';
import ConfirmModal from '../../components/shared/ConfirmModal';

interface PlatformSetting {
  id: string;
  key: string;
  value: string;
  category: string | null;
  description: string | null;
  isSensitive: boolean;
  updatedAt: string;
}

const API_KEY_DEFINITIONS = [
  { key: 'AUDEERING_API_KEY', label: 'audEERING API Key', description: 'Used for audio and speech emotion recognition analysis', category: 'api_keys', isCore: true },
  { key: 'GEMINI_API_KEY', label: 'Google Gemini API Key', description: 'Used for AI-powered analysis and natural language processing via Google Gemini', category: 'api_keys', isCore: true },
  { key: 'OPENAI_API_KEY', label: 'OpenAI API Key', description: 'Used for advanced NLP analysis and transcript processing', category: 'api_keys', isCore: true },
  { key: 'AWS_ACCESS_KEY_ID', label: 'AWS Access Key ID', description: 'Used for S3 video storage', category: 'api_keys', isCore: true },
  { key: 'AWS_SECRET_ACCESS_KEY', label: 'AWS Secret Access Key', description: 'Used for S3 video storage', category: 'api_keys', isCore: true },
];

const FEATURE_FLAG_DEFINITIONS = [
  {
    key: 'platform.featureFlags.oemBenchmarking',
    label: 'OEM Benchmarking',
    description: 'Enables the Benchmarking page and cross-account performance comparison endpoints grouped by dealer group, region, and brand.',
  },
  {
    key: 'platform.featureFlags.partnerApiEnabled',
    label: 'Partner API',
    description: 'Enables partner-facing API routes (/partner/v1/**) and outbound webhook dispatch to third-party systems such as CDK and Reynolds.',
  },
  {
    key: 'platform.featureFlags.outcomeCorrelation',
    label: 'Outcome Correlation',
    description: 'Enables the DMS ↔ video correlation engine that links scored videos to deal outcomes imported from the dealership management system.',
  },
];

const toScreamingSnakeCase = (s: string): string =>
  s.trim()
    .replace(/[\s\-\.\/\\]+/g, '_')
    .replace(/[^A-Z0-9_]/gi, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .toUpperCase();

const DEFINED_KEY_SET = new Set(API_KEY_DEFINITIONS.map((d) => d.key));
const FLAG_KEY_SET = new Set(FEATURE_FLAG_DEFINITIONS.map((d) => d.key));

const PlatformSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<PlatformSetting[]>([]);
  const [loading, setLoading] = useState(true);

  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [savedValues, setSavedValues] = useState<Record<string, string>>({});
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const [showAddKey, setShowAddKey] = useState(false);
  const [customKey, setCustomKey] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [customDesc, setCustomDesc] = useState('');
  const [customSensitive, setCustomSensitive] = useState(true);

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    if (type === 'success') toast.success(message);
    else toast.error(message);
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/settings');
      const data: PlatformSetting[] = res.data.data ?? res.data ?? [];
      setSettings(data);
      const vals: Record<string, string> = {};
      for (const s of data) vals[s.key] = s.value;
      setEditValues(vals);
      setSavedValues(vals);
    } catch {
      showToast('Failed to load settings.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSaveApiKey = async (key: string, definition?: typeof API_KEY_DEFINITIONS[0]) => {
    const value = editValues[key]?.trim();
    if (!value || value.startsWith('••••')) return;
    setSavingKey(key);
    try {
      await api.put('/admin/settings', {
        key,
        value,
        category: definition?.category ?? 'custom',
        description: definition?.description ?? '',
        isSensitive: true,
      });
      showToast(`${key} saved successfully.`, 'success');
      setVisibleKeys((prev) => { const next = new Set(prev); next.delete(key); return next; });
      await fetchSettings();
    } catch {
      showToast(`Failed to save ${key}.`, 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const handleToggleFlag = async (key: string) => {
    const current = settings.find((s) => s.key === key)?.value ?? 'false';
    const next = current === 'true' ? 'false' : 'true';
    const def = FEATURE_FLAG_DEFINITIONS.find((d) => d.key === key)!;
    setSavingKey(key);
    try {
      await api.put('/admin/settings', {
        key,
        value: next,
        category: 'feature_flags',
        description: def.description,
        isSensitive: false,
      });
      showToast(`${def.label} ${next === 'true' ? 'enabled' : 'disabled'}.`, 'success');
      await fetchSettings();
    } catch {
      showToast('Failed to update flag.', 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const handleDelete = async (key: string) => {
    setDeleteConfirm(null);
    try {
      await api.delete(`/admin/settings/${key}`);
      showToast(`${key} removed.`, 'success');
      setSettings((prev) => prev.filter((s) => s.key !== key));
      setEditValues((prev) => { const next = { ...prev }; delete next[key]; return next; });
      setSavedValues((prev) => { const next = { ...prev }; delete next[key]; return next; });
    } catch {
      showToast(`Failed to remove ${key}.`, 'error');
    }
  };

  const handleAddKey = async () => {
    if (!customKey.trim() || !customValue.trim()) return;
    const normalizedKey = toScreamingSnakeCase(customKey);
    if (!normalizedKey) return;
    setSavingKey(customKey);
    try {
      await api.put('/admin/settings', {
        key: normalizedKey,
        value: customValue.trim(),
        category: 'api_keys',
        description: customDesc.trim() || null,
        isSensitive: customSensitive,
      });
      showToast(`${normalizedKey} saved.`, 'success');
      setCustomKey(''); setCustomValue(''); setCustomDesc('');
      setShowAddKey(false);
      await fetchSettings();
    } catch {
      showToast('Failed to save key.', 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const toggleVisibility = (key: string) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const savedKeySet = new Set(settings.map((s) => s.key));
  const customSettings = settings.filter((s) => !DEFINED_KEY_SET.has(s.key) && !FLAG_KEY_SET.has(s.key));

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 transition-colors duration-300 font-sans">
      <div className="flex flex-col">
        <TopNav />
        <main className="flex-1 mx-auto w-full max-w-7xl px-6 py-12">
          <div className="mb-12">
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">Platform Settings</h1>
            <p className="text-slate-500 dark:text-slate-400 font-medium text-base mt-2">
              Manage API keys and platform-wide configuration
            </p>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-32 gap-4">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full shadow-[0_0_10px_rgba(var(--primary-rgb),0.2)]" />
              <span className="text-sm font-semibold text-slate-400 dark:text-slate-500 whitespace-nowrap">Syncing settings...</span>
            </div>
          ) : (
            <div className="space-y-16">

              {/* ── Platform Feature Flags ── */}
              <section className="space-y-8">
                <div className="flex items-center gap-4 border-b border-slate-100 dark:border-white/5 pb-5">
                  <div className="w-10 h-10 rounded-xl bg-violet-500/10 dark:bg-violet-500/20 flex items-center justify-center text-violet-500">
                    <span className="material-symbols-outlined text-[24px]">toggle_on</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">Platform Features</h2>
                    <p className="text-sm text-slate-400 dark:text-slate-500 font-medium mt-0.5">Global on/off switches that apply across all accounts</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {FEATURE_FLAG_DEFINITIONS.map((def) => {
                    const isEnabled = (settings.find((s) => s.key === def.key)?.value ?? 'false') === 'true';
                    const isToggling = savingKey === def.key;

                    return (
                      <div key={def.key} className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl px-8 py-6 flex items-center justify-between gap-8 hover:shadow-md transition-shadow">
                        <div className="space-y-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <h3 className="text-base font-bold text-slate-900 dark:text-white">{def.label}</h3>
                            <span className={`px-2.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-full border ${
                              isEnabled
                                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-800/30'
                                : 'bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-slate-500 border-slate-200 dark:border-white/5'
                            }`}>
                              {isEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                          <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">{def.description}</p>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleToggleFlag(def.key)}
                          disabled={isToggling}
                          className={`relative shrink-0 inline-flex h-7 w-13 items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${
                            isEnabled ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-700'
                          }`}
                          style={{ width: '52px' }}
                        >
                          <span className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${isEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* ── API Keys ── */}
              <section className="space-y-8">
                <div className="flex items-center justify-between gap-4 border-b border-slate-100 dark:border-white/5 pb-5">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary">
                      <span className="material-symbols-outlined text-[24px]">key</span>
                    </div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">API Keys</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAddKey(true)}
                    className="h-12 px-8 rounded-2xl bg-primary text-white font-bold text-xs uppercase tracking-widest shadow-lg shadow-primary/20 hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2"
                  >
                    <span className="material-symbols-outlined text-sm">add</span>
                    Add Key
                  </button>
                </div>

                <div className="bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/30 p-5 rounded-2xl flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
                    <span className="material-symbols-outlined text-base">info</span>
                  </div>
                  <p className="text-sm font-medium text-blue-700 dark:text-blue-400/80 leading-relaxed">
                    API keys are encrypted at rest and masked in the UI.
                    <span className="block mt-0.5 opacity-80 font-normal">Only the last 4 characters are shown after saving for security verification.</span>
                  </p>
                </div>

                <div className="space-y-6">
                  {API_KEY_DEFINITIONS.map((def) => {
                    const saved = savedKeySet.has(def.key);
                    const isVisible = visibleKeys.has(def.key);
                    const isSaving = savingKey === def.key;
                    const currentValue = editValues[def.key] ?? '';

                    return (
                      <div key={def.key} className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-8 gap-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-3">
                              <h3 className="text-lg font-bold text-slate-900 dark:text-white">{def.label}</h3>
                              {saved && (
                                <span className="px-3 py-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold rounded-lg border border-emerald-100 dark:border-emerald-800/30 uppercase tracking-wider">
                                  Configured
                                </span>
                              )}
                            </div>
                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{def.description}</p>
                          </div>
                          {saved && !def.isCore && (
                            <button type="button" onClick={() => setDeleteConfirm(def.key)}
                              className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all cursor-pointer">
                              <span className="material-symbols-outlined text-[20px]">delete</span>
                            </button>
                          )}
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch gap-4">
                          <div className="flex-1 relative group">
                            <input
                              type={isVisible ? 'text' : 'password'}
                              value={currentValue}
                              onChange={(e) => setEditValues({ ...editValues, [def.key]: e.target.value })}
                              placeholder={saved ? 'Enter new value to update' : 'Paste your API key here'}
                              className="w-full h-14 bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 rounded-2xl px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600 transition-all focus:bg-white dark:focus:bg-slate-800 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none font-mono pr-14"
                            />
                            <button type="button" onClick={() => toggleVisibility(def.key)}
                              className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all cursor-pointer">
                              <span className="material-symbols-outlined text-[18px]">{isVisible ? 'visibility_off' : 'visibility'}</span>
                            </button>
                          </div>
                          <button type="button" onClick={() => handleSaveApiKey(def.key, def)}
                            disabled={isSaving || !currentValue.trim() || currentValue === (savedValues[def.key] ?? '')}
                            className="h-14 px-10 rounded-2xl bg-primary hover:bg-primary/90 text-white font-bold shadow-lg shadow-primary/10 disabled:shadow-none disabled:opacity-30 transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-2 min-w-[140px]">
                            {isSaving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Update Key'}
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {customSettings.map((s) => {
                    const isVisible = visibleKeys.has(s.key);
                    const isSaving = savingKey === s.key;
                    const currentValue = editValues[s.key] ?? '';

                    return (
                      <div key={s.key} className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-8 gap-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-3">
                              <h3 className="text-lg font-bold text-slate-900 dark:text-white">{s.key}</h3>
                              <span className="px-3 py-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold rounded-lg border border-emerald-100 dark:border-emerald-800/30 uppercase tracking-wider">
                                Configured
                              </span>
                            </div>
                            {s.description && <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{s.description}</p>}
                          </div>
                          <button type="button" onClick={() => setDeleteConfirm(s.key)}
                            className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all cursor-pointer">
                            <span className="material-symbols-outlined text-[20px]">delete</span>
                          </button>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch gap-4">
                          <div className="flex-1 relative group">
                            <input
                              type={isVisible ? 'text' : 'password'}
                              value={currentValue}
                              onChange={(e) => setEditValues({ ...editValues, [s.key]: e.target.value })}
                              placeholder="Enter new value to update"
                              className="w-full h-14 bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 rounded-2xl px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600 transition-all focus:bg-white dark:focus:bg-slate-800 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none font-mono pr-14"
                            />
                            <button type="button" onClick={() => toggleVisibility(s.key)}
                              className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all cursor-pointer">
                              <span className="material-symbols-outlined text-[18px]">{isVisible ? 'visibility_off' : 'visibility'}</span>
                            </button>
                          </div>
                          <button type="button" onClick={() => handleSaveApiKey(s.key)}
                            disabled={isSaving || !currentValue.trim() || currentValue === (savedValues[s.key] ?? '')}
                            className="h-14 px-10 rounded-2xl bg-primary hover:bg-primary/90 text-white font-bold shadow-lg shadow-primary/10 disabled:shadow-none disabled:opacity-30 transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-2 min-w-[140px]">
                            {isSaving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Update Key'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              <PartnerIntegrationsPanel />
              <ScoringProfilesPanel />
            </div>
          )}
        </main>

        <ConfirmModal
          open={deleteConfirm !== null}
          title="Delete API Key"
          message={`Are you sure you want to delete ${deleteConfirm ?? ''}? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />

        {/* Add API Key Modal */}
        {showAddKey && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
              <div className="px-6 py-5 sm:px-8 sm:pt-8 sm:pb-6 bg-slate-50/50 dark:bg-white/5 border-b border-slate-100 dark:border-white/5 flex items-center justify-between gap-4 shrink-0">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary">
                    <span className="material-symbols-outlined text-[24px]">key</span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Add API Key</h2>
                </div>
                <button type="button"
                  onClick={() => { setShowAddKey(false); setCustomKey(''); setCustomValue(''); setCustomDesc(''); }}
                  className="w-8 h-8 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-all">
                  <span className="material-symbols-outlined text-lg font-bold">close</span>
                </button>
              </div>

              <div className="px-6 py-8 sm:px-10 sm:py-10 space-y-8 overflow-y-auto thin-scrollbar">
                <div className="p-4 bg-amber-50/60 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800/30 rounded-xl flex items-start gap-3">
                  <span className="material-symbols-outlined text-amber-500 text-[18px] mt-0.5 shrink-0">info</span>
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-400/80 leading-relaxed">
                    Your key name will be auto-converted to <span className="font-bold font-mono">SCREAMING_SNAKE_CASE</span> before saving. This is the exact name used in backend code to retrieve the value — e.g. <span className="font-mono">platformSettingsService.getValue('MY_SERVICE_API_KEY')</span>.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2.5">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">Key Name <span className="text-red-500">*</span></label>
                    <input type="text" value={customKey} onChange={(e) => setCustomKey(e.target.value)} placeholder="e.g. My Service API Key"
                      className="h-12 sm:h-14 w-full rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800 px-6 text-sm font-bold text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600 transition-all focus:bg-white dark:focus:bg-slate-800 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none font-mono" />
                    {customKey.trim() && (
                      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 ml-1">
                        Saved as: <span className="font-bold font-mono text-primary">{toScreamingSnakeCase(customKey) || '—'}</span>
                      </p>
                    )}
                  </div>
                  <div className="space-y-2.5">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">Key Value <span className="text-red-500">*</span></label>
                    <input type="text" value={customValue} onChange={(e) => setCustomValue(e.target.value)} placeholder="sk-..."
                      className="h-12 sm:h-14 w-full rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800 px-6 text-sm font-bold text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600 transition-all focus:bg-white dark:focus:bg-slate-800 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none font-mono" />
                  </div>
                </div>
                <div className="space-y-2.5">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">Description (Optional)</label>
                  <input type="text" value={customDesc} onChange={(e) => setCustomDesc(e.target.value)} placeholder="Describe the purpose of this key..."
                    className="h-12 sm:h-14 w-full rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800 px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600 transition-all focus:bg-white dark:focus:bg-slate-800 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none" />
                </div>
                <div className="p-6 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5 rounded-2xl flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="text-sm font-bold text-slate-900 dark:text-white">Sensitive Value</div>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Mask this key's value in the dashboard interface</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={customSensitive} onChange={(e) => setCustomSensitive(e.target.checked)} className="sr-only peer" />
                    <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>

              <div className="px-6 py-6 sm:px-10 sm:py-8 flex items-center justify-end gap-4 border-t border-slate-100 dark:border-white/5 shrink-0">
                <button type="button"
                  onClick={() => { setShowAddKey(false); setCustomKey(''); setCustomValue(''); setCustomDesc(''); }}
                  className="h-12 px-8 rounded-xl font-bold text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300 transition-all cursor-pointer">
                  Discard
                </button>
                <button type="button" onClick={handleAddKey}
                  disabled={savingKey === customKey || !customKey.trim() || !customValue.trim()}
                  className="h-12 px-10 rounded-xl font-bold bg-primary hover:bg-primary/90 text-on-primary shadow-lg shadow-primary/10 hover:shadow-xl hover:shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer whitespace-nowrap disabled:opacity-30 disabled:shadow-none disabled:scale-100">
                  {savingKey === customKey ? 'Saving...' : 'Add Key'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformSettingsPage;
